import React, { useEffect, useState } from "react";
import { FilterSection, RfqsTableSection } from "./Components";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import {
  ADD_VENDORS_RFQ_SUCCESS,
  FilterRfqByDate,
  fetchRfqProjects,
  fetchRfqs,
  fetchSearchRfqs,
  getApprovedRfqs,
  saveRfq,
  saveRfqNew,
  searchRfqWithDate,
} from "actions/rfqActions";
import SelectVendorsSlider from "./Components/SelectVendorsSlider";
import RfqVendorRecommendations from "./Components/RfqVendorRecommendations";
import SidePanel from "layouts/SidePanel";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { FormControl, MenuItem, Select } from "@mui/material";
import DateRangeComponent from "./DateRangeComponent";
import DateRangePicker from "./DateRangePicker ";
import Button from "../../components/Button";
import SlideInForm from "./Components/SlideInForm";
import RfqTypeMaterial from "./Components/RfqTypeMaterial";
import GeneralInformationNew from "./Components/GeneralInformationNew";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LoadingModal from "./LoadingModal";
import RFQTypeDropDown from "./Components/RFQTypeDropDown";
import { signout } from "actions/authActions";
import MobilePendingApprovalsView from "./Components/VendorBidding/MobilePendingApprovalsView";
import {
  getAuthorizedDashboardEntityId,
  getCurrentUserEntityName,
  getEntityDashboardPath,
  hasPendingApprovalsAccess,
} from "views/Dashboard/utils/dashboardAccess";

const validationSchema = Yup.object({
  rfqName: Yup.string().required("Required"),
  category: Yup.mixed().when("requirementType", {
    is: (requirementType) => ["material", "labour", "contractor"].includes(requirementType),
    then: Yup.mixed().test(
      "taxonomy-category-required",
      "Please select RFQ subject from category suggestions.",
      (value) => Boolean(value?.categoryId)
    ),
    otherwise: Yup.mixed().nullable(),
  }),
  requirementType: Yup.string().required("Required"),
  emailCopyRecipients: Yup.array()
    .of(Yup.string().email("Invalid email format"))
    .required("Required"),
  projectName: Yup.string().required("Required"),
  businessDivision: Yup.string().when("isBusinessDivisionRequired", {
    is: true,
    then: Yup.string().required("Required"),
    otherwise: Yup.string().notRequired(),
  }),
  providingDate: Yup.date(),
  rfqBudget: Yup.string()
    .matches(/^\d*\.?\d*$/, "Budget must be a valid number"),
  materials: Yup.array().when("requirementType", {
    is: "material",
    then: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string().required("Required"),
          quantity: Yup.number()
            .typeError("Invalid quantity")
            .required("Required")
            .moreThan(0, "Invalid quantity")
            .test(
              'is-valid-number',
              "Invalid quantity",
              (value) => {
                // Allow any positive number including decimals
                return value > 0;
              }
            ),
          unit: Yup.string().required("Required"),
        })
      )
      .min(1, "At least one material is required")
      .required("Materials are required"),
    otherwise: Yup.array().notRequired(),
  }),
  labourDetails: Yup.array().when("requirementType", {
    is: "labour",
    then: Yup.array()
      .of(
        Yup.object().shape({
          skill: Yup.string().required("Required"),
          quantity: Yup.number().required("Required"),
            // .min(1, "Must be at least 1"),
            unit: Yup.string().required("Required"),
        })
      )
      .required("Labour details are required")
      .min(1, "At least one labour detail is required"),
    otherwise: Yup.array().notRequired(),
  }),
  providingFixingDetails: Yup.array().when("requirementType", {
    is: "contractor",
    then: Yup.array()
      .of(
        Yup.object().shape({
          sectionName: Yup.string().required("Section Name is required"),
          scopes: Yup.array()
            .of(
              Yup.object().shape({
                scopeOfWork: Yup.string().required("Required"),
                quantity: Yup.number().when("materials", {
                  is: (materials) => materials.length === 0,
                  then: Yup.number()
                    .typeError("Invalid")
                    .required("Required")
                    .min(0, "Invalid")
                    .test(
                      'is-valid-number',
                      "Invalid",
                      (value) => value >= 0  // Allows 0 and positive decimals
                    ),
                  otherwise: Yup.number().notRequired(),
                }),
                unit: Yup.string().when("materials", {
                  is: (materials) => materials.length === 0,
                  then: Yup.string().required("Required"),
                  otherwise: Yup.string().notRequired(),
                }),
                materials: Yup.array()
                  .of(
                    Yup.object().shape({
                      name: Yup.string().required("Required"),
                      quantity: Yup.number()
                        .typeError("Invalid")
                        .required("Required")
                        .min(0, "Invalid"),
                      unit: Yup.string().required("Required"),
                    })
                  )
                  .notRequired(),
              })
            )
            .min(1, "At least one scope is required")
            .required("Scopes are required"),
        })
      )
      .min(1, "At least one section is required")
      .required("Providing & Fixing Details are required"),
    otherwise: Yup.array().notRequired(),
  }),

  otherCharges: Yup.object().test(
    "all-charges-selected",
    "Please select Yes or No for all additional cost components.",
    function (value) {
      const { requirementType } = this.parent;
      let requiredCharges = [];

      if (requirementType === "material" || requirementType === "contractor") {
        requiredCharges = ["transport", "loading", "unLoading", "mathadeCharges", "miscCharges"];
      } else if (requirementType === "labour") {
        requiredCharges = ["mathadeCharges", "miscCharges"];
      }

      return requiredCharges.every((key) => value && value[key] !== undefined);
    }
  ),

  transportOption: Yup.string().when(["otherCharges.transport", "requirementType", "advancedTransportOptions"], {
    is: (transport, requirementType, advancedTransportOptions) => transport === true && requirementType === "material" && advancedTransportOptions,
    then: Yup.string().required("Transport Type is required"),
    otherwise: Yup.string().notRequired(),
  }),

});

const capitalizeFirstLetter = (string) => {
  if (!string) return "";
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

const hasEntityFeature = (features = [], featureName = "") =>
  features.some(
    (feature) =>
      String(feature?.featureName || "").toLowerCase() === featureName.toLowerCase() &&
      feature?.hasFeature === true
  );

const normalizeWorkspaceStatus = (status) => {
  if (status === null || status === undefined) return null;

  const decodedStatus = decodeURIComponent(String(status || "")).trim().toUpperCase();
  const validStatuses = new Set(["DRAFT", "OPEN", "PENDING APPROVAL", "REWORK", "CLOSED", "DELETED"]);

  if (decodedStatus === "ALL") return "";
  return validStatuses.has(decodedStatus) ? decodedStatus : "";
};

const normalizeWorkspaceRfqTypes = (rfqType) => {
  if (!rfqType) return [];

  const validTypes = new Set(["material", "contractor", "labour"]);
  return String(rfqType)
    .split(",")
    .map((type) => type.trim().toLowerCase())
    .filter((type) => validTypes.has(type));
};

const normalizeWorkspaceCategoryFilter = (queryParams) => {
  const categoryFilter = {
    disciplineId: String(queryParams.get("disciplineId") || "").trim(),
    categoryId: String(queryParams.get("categoryId") || "").trim(),
    subCategoryId: String(queryParams.get("subCategoryId") || "").trim(),
  };

  return Object.values(categoryFilter).some(Boolean) ? categoryFilter : null;
};


const getWorkspaceMemberId = (member) => {
  if (member && typeof member === "object") return String(member.userId || member.id || member._id || "").trim();
  return String(member || "").trim();
};

const getWorkspaceEntityMemberIds = (user = {}) => {
  const members = Array.isArray(user?.userEntity?.members) ? user.userEntity.members : [];
  const memberIds = members.map(getWorkspaceMemberId).filter(Boolean);
  const currentUserId = String(user?.userId || "").trim();
  if (currentUserId) memberIds.push(currentUserId);
  return [...new Set(memberIds)];
};
const RfqsSections = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  // const rfqData = useSelector((state) => state.rfq);
  const rfqsData = useSelector((state) => state.rfqs);
  // console.log("rfqData", rfqsData)
  const auth = useSelector((state) => state.auth);
  const projectData = useSelector((state) => state.rfqProjects);

  const pendingApprovalsCount = rfqsData.rfqs.filter(
    (rfq) => rfq.status === "PENDING APPROVAL"
  ).length;

  const projects = projectData?.projects ?? [];
  const user = auth?.user;
  const dashboardEntityId = getAuthorizedDashboardEntityId(user);
  const dashboardEntityName = getCurrentUserEntityName(user);
  const hasDashboardAccess = Boolean(dashboardEntityId);

  const fetchApprovedRfqs = () => {
    setSelectedBusinessDivisions([]);
    setWorkspaceProjectId("");
    setWorkspaceCategoryFilter(null);
    setSelectedProjectNames([]);
    setWorkspaceView("Approved RFQs");
    setRfqFilterStatus(""); // Clear status filter since we're filtering by approver
    setStartDate("");
    setEndDate(today);
    setDateFilter(false);
    navigate("/rfqs/workspace/approved-by-me");

    // Fetch all RFQs first, then filter by approver on frontend
    dispatch(
      searchRfqWithDate({
        entityId,
        dateFilterIsActive: rfqsData?.dateFilterIsActive,
        startDate: rfqsData?.startDate,
        endDate: rfqsData?.endDate,
        projectId: workspaceProjectId,
        projectNames: selectedProjectNames.length > 0 ? selectedProjectNames : undefined,
        categoryFilter: workspaceCategoryFilter,
        searchParam: rfqsData?.searchParam,
        status: "", // Don't filter by status
        userIds: [...teamUserIds, user?.userId],
        closeDateFilterIsActive: rfqsData?.closeDateFilterIsActive,
        startDateOfClosing: rfqsData?.startDateOfClosing,
        endDateOfClosing: rfqsData?.endDateOfClosing,
        rfqTypes: selectedRfqTypes.length > 0 ? selectedRfqTypes : undefined,
        approvedByUserId: user?.userId,
        workspaceType: "APPROVED_BY_ME",
        approverId: user?.userId,
        teamUserIds: teamUserIdsInString,
      })
    );
    setTeamMemberData([...teamUserIds, user?.userId]);
  };

  const isApprovalRequired = user?.userEntity?.features?.some(
    (feature) => feature.featureName === "APPROVAL_FLOW" && feature.hasFeature
  );
  const canAccessPendingApprovals = hasPendingApprovalsAccess(user);

  const showBusinessDivisions = user?.userEntity?.businessDivisions?.length > 0 || false;

  const marketPlaceAccess = user?.userEntity?.features?.some(
    (feature) => feature.featureName === "VENDOR_MARKETPLACE" && feature.hasFeature === true
  ) || false;

  const advancedTransportOptions = user?.userEntity?.features?.some(
    (feature) => feature.featureName === "TRANSPORT_OPTIONS" && feature.hasFeature === true
  ) || false;

  const businessDivisionOptions = user?.userEntity?.businessDivisions?.map(division => ({
    value: division,
    label: `${division}`,
  })) || [];

  const multipleProjectSelection = user?.userEntity?.features?.some(
    (feature) => feature.featureName === "MULTIPLE_PROJECTS" && feature.hasFeature === true
  ) || false;
  const indentIntegrationEnabled = hasEntityFeature(
    user?.userEntity?.features,
    "integration"
  );
  const integrationData = user?.userEntity?.integrationData || {};
  const materialFetchMode = integrationData?.materialFetchMode || "indent";
  const indentApiUrl = integrationData?.indentApiUrl || "";
  const pendingPrApiUrl = integrationData?.pendingPrApiUrl || integrationData?.pendingPrApi?.url || "";
  const isIndentMaterialIntegration =
    indentIntegrationEnabled && materialFetchMode !== "pendingPr" && Boolean(indentApiUrl);
  const isPendingPrMaterialIntegration =
    indentIntegrationEnabled && materialFetchMode === "pendingPr" && Boolean(pendingPrApiUrl);
  const materialMasterEnabled = hasEntityFeature(user?.userEntity?.features, "MATERIAL_MASTER_LIST");

  const maxVendorsAllowed = user?.userEntity?.limits?.RFQVendors ?? 15;

  const currentPage = location.pathname;
  const [rfqData, setRfqData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [workspaceView, setWorkspaceView] = useState(canAccessPendingApprovals
    ? "Pending Approvals"
    : "My Workspace");
  const [workspaceDropDownValue, setWorkspaceDropDownValue] = useState("All");
  const [hasMoreOptions, setHasMoreOptions] = useState(false);
  const [rfqFilterStatus, setRfqFilterStatus] = useState(canAccessPendingApprovals ? "PENDING APPROVAL" : "OPEN");
  const [workspaceProjectId, setWorkspaceProjectId] = useState("");
  const [workspaceCategoryFilter, setWorkspaceCategoryFilter] = useState(null);
  const [selectedProjectNames, setSelectedProjectNames] = useState([]);
  const [showFiltersPanel, setShowFiltersPanel] = useState(true);
  const [rfqSearchValue, setRfqSearchValue] = useState(rfqsData?.searchParam ?? "");
  const [rfqSearchKey, setRfqSearchKey] = useState(rfqsData?.searchParam ?? "");
  const [showForm, setShowForm] = useState(false);
  const [openSlider, setOpenSlider] = useState(false);
  const [openRecommendations, setOpenRecommendations] = useState(false);
  const [pendingInviteAfterSave, setPendingInviteAfterSave] = useState(false);
  const [pendingRecommendationCategory, setPendingRecommendationCategory] = useState(null);
  const [recommendedSelectedVendors, setRecommendedSelectedVendors] = useState([]);
  const [pendingPrFetchRequest, setPendingPrFetchRequest] = useState(0);
  const [materialMasterFetchRequest, setMaterialMasterFetchRequest] = useState(0);
  const [openAddVendorOption, setOpenAddVendorOption] = useState(false);
  const [sliderName, setSliderName] = useState("");
  const [selectedFilesBase, setSelectedFilesBase] = useState([]);
  const [teamMemberData, setTeamMemberData] = useState([user?.userId]);
  const [dateFilter, setDateFilter] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [clearFilter, setClearFilter] = useState({ status: false, name: "" });

  const handleOpenDashboard = () => {
    if (!dashboardEntityId) return;
    navigate(getEntityDashboardPath(dashboardEntityId, dashboardEntityName));
  };
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedRequirementType, setSelectedRequirementType] = useState("");
  const [selectedRfqTypes, setSelectedRfqTypes] = useState([]);
  const [selectedBusinessDivisions, setSelectedBusinessDivisions] = useState([]);
  const getDefaultRfqFormValues = () => ({
    rfqName: "",
    category: null,
    requirementType: "",
    providingDate: "",
    emailCopyRecipients: [],
    attachments: [],
    projectName: "",
    subProjectIds: [],
    isBusinessDivisionRequired: businessDivisionOptions.length > 0,
    businessDivision: "",
    rfqDetails: "",
    materials: [
      {
        code: "",
        name: "",
        quantity: "",
        unit: "",
        budget: 0,
        brands: [],
        specifications: "",
      },
    ],
    otherCharges: {},
    transportOption: "",
    labourDetails: [],
    paymentTerms: "",
    termsAndConditions: "",
    advancedTransportOptions: advancedTransportOptions,
    providingFixingDetails: [
      {
        sectionName: "",
        scopes: [
          {
            scopeOfWork: "",
            quantity: "",
            unit: "",
            preferredBrands: [],
            materials: [],
          },
        ],
      },
    ],
    displayRanks: false,
    parentsRefId: "",
    quotationDeadline: "",
    rfqBudget: "",
  });
  const [rfqFormValues, setRfqFormValues] = useState(getDefaultRfqFormValues());

  const getWorkspaceType = (workspaceView) => {
    switch (workspaceView) {
      case "My Workspace":
        return "MY_WORKSPACE";
      case "Team Workspace":
        return "TEAM_WORKSPACE";
      case "Pending Approvals":
        return "PENDING_APPROVALS";
      case "Approved RFQs":
        return "APPROVED_BY_ME";
      default:
        return "MY_WORKSPACE";
    }
  };

  const today = new Date().toISOString().split("T")[0];
  // const loading = rfqData?.loading;
  const entityId = user?.userEntity?.entityId;
  const entityName = user?.userEntity?.entityName;
  const directoryName = user?.userEntity?.directoryName;
  const userId = user?.userId;
  const teamUserIds = user?.teamMembers?.map((member) => member.userId);
  const teamUserIdsInString = [...(user?.teamMembers?.map((m) => String(m.userId)) || []), String(userId),];

  const updateRfqFormValues = (updatedValues) => {
    setRfqFormValues(updatedValues);
  };

  const handleRequirementTypeSelect = (type) => {
    setRfqFormValues((prevValues) => ({
      ...prevValues,
      attachments: [],
      otherCharges: {},
      labourDetails: [],
      paymentTerms: "",
      materials: [
        {
          code: "",
          name: "",
          quantity: "",
          unit: "",
          budget: 0,
          brands: [],
          specifications: "",
        },
      ],
      providingFixingDetails: [
        {
          sectionName: "",
          scopes: [
            {
              scopeOfWork: "",
              quantity: "",
              unit: "",
              preferredBrands: [],
              materials: [],
            },
          ],
        },
      ],
      requirementType: type, // Update requirementType in state
    }));
    setShowDropdown(false);
    navigate(`/rfqs/create?rfqType=${type}`); // Update the URL
    setShowForm(true); // Open the form
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const numericBudget = parseFloat(values?.rfqBudget);
    const safeBudget = isNaN(numericBudget) ? 0 : numericBudget;
    const formValue = {
      ...values,
      projectId: values.projectName,
      rfqBudget: safeBudget,
      businessDivision: values?.businessDivision || (entityId === "ent_36" ? "" : undefined),
    };

    setLoading(true); // Show loading spinner

    try {
      const saveResult = await saveRfqNew({ entityId, values: formValue });

      // If save is successful, set the RFQ data
      if (saveResult.error) {
        setError(saveResult.message); // Set the error message if error is true
      } else {
        setRfqData(saveResult.payload); // Set RFQ data to state
        dispatch({
        type: ADD_VENDORS_RFQ_SUCCESS,
        payload: { rfq: saveResult.payload },
      });
        setError(null); // Reset error if the RFQ is saved successfully
      }
    } catch (error) {
      console.error("Error saving RFQ:", error);
      setError("Failed to save RFQ. Please try again.");
    } finally {
      setLoading(false); // Close the loading spinner
    }
  };
  const toggleForm = () => {
    setShowForm(!showForm);
    if (showForm) {
      navigate("/rfqs");
    } else {
      navigate("/rfqs/create");
    }
  };

  const handleSliderOpen = (title) => {
    setSliderName(title);
    setOpenSlider(true);
  };
  const handleSliderClose = () => {
    setOpenSlider(false);
  };

  useEffect(() => {
    if (!pendingInviteAfterSave || !rfqData?.rfqId) return;

    setPendingInviteAfterSave(false);
    setOpenRecommendations(true);
  }, [pendingInviteAfterSave, pendingRecommendationCategory, rfqData?.rfqId]);

  const handleCloseRecommendations = () => {
    setOpenRecommendations(false);
    setRecommendedSelectedVendors([]);
  };

  const handleContinueWithRecommendedVendors = (vendors = []) => {
    setOpenRecommendations(false);
    setRecommendedSelectedVendors(vendors);

    const hasInternalVendor = vendors.some((vendor) => vendor?.source !== "MARKETPLACE");
    handleSliderOpen(hasInternalVendor ? "CD" : "NN");
  };

  const handleGetRfqs = ({
    status = "",
    searchKey = rfqsData?.searchParam,
  }) => {
    const hasWorkspaceCategoryFilter = Boolean(workspaceCategoryFilter);
    const hasWorkspaceRfqTypeFilter = selectedRfqTypes.length > 0;
    const hasProjectNameFilter = selectedProjectNames.length > 0;

    if (
      searchKey === "" &&
      startDate === "" &&
      endDate === "" &&
      !workspaceProjectId &&
      !hasProjectNameFilter &&
      !hasWorkspaceCategoryFilter &&
      !hasWorkspaceRfqTypeFilter
    ) {
      dispatch(fetchRfqs({ entityId, userIds: teamMemberData, status }));
      return;
    }
    if (startDate != "" && endDate != "") {
      fetchFilterDateRfq({ page: 1, status, searchKey });
    } else {
      // dispatch(fetchRfqs({ entityId, userIds: teamMemberData, status }));
      dispatch(
        (workspaceProjectId || hasProjectNameFilter || hasWorkspaceCategoryFilter || hasWorkspaceRfqTypeFilter) ? searchRfqWithDate({
          entityId,
          projectId: workspaceProjectId,
          projectNames: selectedProjectNames.length > 0 ? selectedProjectNames : undefined,
          searchParam: searchKey,
          status,
          userIds: teamMemberData,
          rfqTypes: selectedRfqTypes.length > 0 ? selectedRfqTypes : undefined,
          categoryFilter: workspaceCategoryFilter,
          workspaceType: getWorkspaceType(workspaceView),
          approverId: user?.userId,
          teamUserIds: teamUserIdsInString,
        }) : fetchSearchRfqs({
          entityId,
          searchParam: searchKey,
          status,
          userIds: teamMemberData,
        })
      );
    }
  };
  const handleCloseRfqForm = () => {
    setRfqFormValues(getDefaultRfqFormValues());
    fetchPersonalRfqs();
    setOpenAddVendorOption(false);
    setOpenRecommendations(false);
    setPendingInviteAfterSave(false);
    setPendingRecommendationCategory(null);
    setRecommendedSelectedVendors([]);
  };

  const handleChangeTeamMember = (event) => {
    const selectedUserId = event.target.value;
    setStartDate("");
    setClearFilter({ status: true, name: "team member" });
    setEndDate(today);
    setDateFilter(false);
    setWorkspaceDropDownValue(selectedUserId);
    if (selectedUserId === "All") {
      fetchAllRfqs();
    } else if (selectedUserId === "Team") {
      dispatch(
        fetchRfqs({ entityId, userIds: teamUserIds, status: rfqFilterStatus })
      );
      setTeamMemberData(teamUserIds);
    } else {
      dispatch(
        fetchRfqs({
          entityId,
          userIds: [selectedUserId],
          status: rfqFilterStatus,
        })
      );
      setTeamMemberData([event.target.value]);
    }
  };

  const handleChangeTeamMemberNew = (selectedUserId) => {
    setRfqFilterStatus("");

    dispatch(
      searchRfqWithDate({
        entityId,
        dateFilterIsActive: rfqsData?.dateFilterIsActive,
        startDate: rfqsData?.startDate,
        endDate: rfqsData?.endDate,
        searchParam: rfqsData?.searchParam,
        status: "",
        userIds:
          selectedUserId.length == 0
            ? [...teamUserIds, user?.userId]
            : selectedUserId,
        closeDateFilterIsActive: rfqsData?.closeDateFilterIsActive,
        startDateOfClosing: rfqsData?.startDateOfClosing,
        endDateOfClosing: rfqsData?.endDateOfClosing,
        rfqTypes: selectedRfqTypes.length > 0 ? selectedRfqTypes : undefined,
        workspaceType: "TEAM_WORKSPACE",
        approverId: user?.userId,
        teamUserIds: teamUserIdsInString,
      })
    );
    setTeamMemberData(selectedUserId);
  };

  const fetchAllRfqs = () => {
    setSelectedBusinessDivisions([]);
    setWorkspaceProjectId("");
    setWorkspaceCategoryFilter(null);
    setSelectedProjectNames([]);
    setWorkspaceView("Team Workspace");
    setRfqFilterStatus("OPEN");
    setSelectedRfqTypes([]);
    navigate("/rfqs/workspace/team-workspace");

    setStartDate("");
    // setClearFilter({ status: true, name: "all rfq" });
    setEndDate(today);
    setDateFilter(false);
    dispatch(
      searchRfqWithDate({
        entityId,
        dateFilterIsActive: rfqsData?.dateFilterIsActive,
        startDate: rfqsData?.startDate,
        endDate: rfqsData?.endDate,
        searchParam: rfqsData?.searchParam,
        status: "OPEN",
        userIds: [...teamUserIds, user?.userId],
        closeDateFilterIsActive: rfqsData?.closeDateFilterIsActive,
        startDateOfClosing: rfqsData?.startDateOfClosing,
        endDateOfClosing: rfqsData?.endDateOfClosing,
        rfqTypes: selectedRfqTypes.length > 0 ? selectedRfqTypes : undefined,
        workspaceType: "TEAM_WORKSPACE",
        approverId: user?.userId,
        teamUserIds: teamUserIdsInString,
      })
    );
    setTeamMemberData([...teamUserIds, user?.userId]);
    setWorkspaceDropDownValue("All");

  };

  const fetchPendingApprovalRfqs = () => {
    setSelectedBusinessDivisions([]);
    setWorkspaceProjectId("");
    setWorkspaceCategoryFilter(null);
    setSelectedProjectNames([]);
    setWorkspaceView("Pending Approvals");
    setRfqFilterStatus("PENDING APPROVAL");
    setStartDate("");
    // setClearFilter({ status: true, name: "all rfq" });
    setEndDate(today);
    setDateFilter(false);
    navigate("/rfqs/workspace/pending-approvals");
    dispatch(
      searchRfqWithDate({
        entityId,
        dateFilterIsActive: rfqsData?.dateFilterIsActive,
        startDate: rfqsData?.startDate,
        endDate: rfqsData?.endDate,
        searchParam: rfqsData?.searchParam,
        status: "PENDING APPROVAL",
        userIds: [...teamUserIds, user?.userId],
        closeDateFilterIsActive: rfqsData?.closeDateFilterIsActive,
        startDateOfClosing: rfqsData?.startDateOfClosing,
        endDateOfClosing: rfqsData?.endDateOfClosing,
        rfqTypes: selectedRfqTypes.length > 0 ? selectedRfqTypes : undefined,
        workspaceType: "PENDING_APPROVALS",
        approverId: user?.userId,
        teamUserIds: teamUserIdsInString,
      })
    );
    setTeamMemberData([...teamUserIds, user?.userId]);
    // setWorkspaceDropDownValue("All");
  };

  const fetchPersonalRfqs = () => {
    setSelectedBusinessDivisions([]);
    setWorkspaceProjectId("");
    setWorkspaceCategoryFilter(null);
    setSelectedProjectNames([]);
    setWorkspaceView("My Workspace");
    setStartDate("");
    setRfqFilterStatus("")
    navigate("/rfqs/workspace/my-workspace");
    // setClearFilter({ status: true, name: "fetchPersonalRfqs" });
    // setEndDate(today);
    // setDateFilter(false);

    dispatch(
      searchRfqWithDate({
        entityId,
        dateFilterIsActive: rfqsData?.dateFilterIsActive,
        startDate: rfqsData?.startDate,
        endDate: rfqsData?.endDate,
        searchParam: rfqsData?.searchParam,
        status: rfqsData?.filterStatus || "OPEN",
        userIds: [userId],
        closeDateFilterIsActive: rfqsData?.closeDateFilterIsActive,
        startDateOfClosing: rfqsData?.startDateOfClosing,
        endDateOfClosing: rfqsData?.endDateOfClosing,
        rfqTypes: selectedRfqTypes.length > 0 ? selectedRfqTypes : undefined,
        workspaceType: "MY_WORKSPACE",
        approverId: user?.userId,
        teamUserIds: teamUserIdsInString,
      })
    );
    setTeamMemberData([user?.userId]);
    setWorkspaceDropDownValue("All");
  };

  const fetchFilterDateRfq = ({
    page = 1,
    status = rfqFilterStatus,
    searchKey,
  } = {}) => {
    dispatch(
      searchRfqWithDate({
        entityId,
        projectId: workspaceProjectId,
        projectNames: selectedProjectNames.length > 0 ? selectedProjectNames : undefined,
        searchParam: searchKey,
        status,
        userIds: teamMemberData.map((number) => String(number)),
        startDate: rfqsData?.startDate,
        endDate: rfqsData?.endDate,
        rfqTypes: selectedRfqTypes.length > 0 ? selectedRfqTypes : undefined,
        categoryFilter: workspaceCategoryFilter,
        workspaceType: getWorkspaceType(workspaceView),
        approverId: user?.userId,
        teamUserIds: teamUserIdsInString,
      })
    );
  };

  const handleRfqSearchChange = async (e) => {
    const value = e.target.value;
    setRfqSearchValue(value);

    if (value !== "") return;

    await dispatch(
      searchRfqWithDate({
        entityId,
        projectId: workspaceProjectId,
        projectNames: selectedProjectNames.length > 0 ? selectedProjectNames : undefined,
        categoryFilter: workspaceCategoryFilter,
        searchParam: "",
        status: "",
        userIds: teamMemberData.map((number) => String(number)),
        startDate: rfqsData?.startDate,
        endDate: rfqsData?.endDate,
        startDateOfClosing: rfqsData?.startDateOfClosing,
        endDateOfClosing: rfqsData?.endDateOfClosing,
        dateFilterIsActive: rfqsData?.dateFilterIsActive,
        closeDateFilterIsActive: rfqsData?.closeDateFilterIsActive,
        rfqTypes: selectedRfqTypes.length > 0 ? selectedRfqTypes : undefined,
        businessDivision: selectedBusinessDivisions.length > 0 ? selectedBusinessDivisions : undefined,
        workspaceType: getWorkspaceType(workspaceView),
        approverId: user?.userId,
        teamUserIds: teamUserIdsInString,
      })
    );
    setRfqSearchKey("");
  };

  const handleSearchRfqs = async () => {
    if (rfqSearchValue.trim()) {
      setRfqFilterStatus("");
    }
    await dispatch(
      searchRfqWithDate({
        entityId,
        projectId: workspaceProjectId,
        projectNames: selectedProjectNames.length > 0 ? selectedProjectNames : undefined,
        categoryFilter: workspaceCategoryFilter,
        searchParam: rfqSearchValue,
        status: rfqSearchValue.trim() ? "" : rfqFilterStatus,
        userIds: teamMemberData.map((number) => String(number)),
        startDate: rfqsData?.startDate,
        endDate: rfqsData?.endDate,
        startDateOfClosing: rfqsData?.startDateOfClosing,
        endDateOfClosing: rfqsData?.endDateOfClosing,
        dateFilterIsActive: rfqsData?.dateFilterIsActive,
        closeDateFilterIsActive: rfqsData?.closeDateFilterIsActive,
        rfqTypes: selectedRfqTypes.length > 0 ? selectedRfqTypes : undefined,
        businessDivision: selectedBusinessDivisions.length > 0 ? selectedBusinessDivisions : undefined,
        workspaceType: getWorkspaceType(workspaceView),
        approverId: user?.userId,
        teamUserIds: teamUserIdsInString,
      })
    );
    setRfqSearchKey(rfqSearchValue);
  };

  const handleRfqSearchKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    handleSearchRfqs();
  };

  const getShowingRfqsLabel = () => {
    const rfqCount = rfqsData?.rfqs?.length || 0;
    if (!rfqCount || !rfqsData?.currentPage) return "";

    const start = (rfqsData.currentPage - 1) * 100 + 1;
    const end = start + rfqCount - 1;
    const statusCount = rfqFilterStatus
      ? rfqsData?.statusWiseCounts?.find((item) => item?._id === rfqFilterStatus)?.count
      : undefined;
    const total = statusCount ?? rfqsData?.count ?? rfqsData?.totalCount ?? rfqCount;
    const suffix = rfqSearchKey ? ` for "${rfqSearchKey}"` : "";
    return `Showing ${start}-${end} of ${total} RFQ${rfqCount > 1 ? "s" : ""}${suffix}`;
  };

  const handleApplyFilterDate = ({ startDate, endDate }) => {
    setStartDate(startDate);
    setEndDate(endDate);
    // dispatch(FilterRfqByDate(payload));
    dispatch(
      searchRfqWithDate({
        entityId,
        searchParam: rfqsData?.searchParam,
        projectNames: selectedProjectNames.length > 0 ? selectedProjectNames : undefined,
        status: rfqFilterStatus,
        userIds: teamMemberData.map((number) => String(number)),
        startDate,
        endDate,
        rfqTypes: selectedRfqTypes.length > 0 ? selectedRfqTypes : undefined,
        categoryFilter: workspaceCategoryFilter,
        workspaceType: getWorkspaceType(workspaceView),
        approverId: user?.userId,
        teamUserIds: teamUserIdsInString,
      })
    );
  };
  const clearDateFilter = () => {
    // dispatch(
    //   fetchRfqs({ entityId, userIds: [userId], status: rfqFilterStatus })
    // );
    dispatch(
      fetchSearchRfqs({
        entityId,
        searchParam: rfqsData?.searchParam,
        status: rfqFilterStatus,
        userIds: [userId],
      })
    );
    setStartDate("");
    setEndDate("");
  };

  useEffect(() => {
    if (!user || !entityId) return;
    if (user.userType !== "buyer") {
      navigate("/leads");
    };

    const userIdString = String(user?.userId);
    const teamUserIdsInString = [
      ...(user?.teamMembers?.map((m) => String(m.userId)) || []),
      userIdString,
    ];

    const canOpenPendingApprovals = hasPendingApprovalsAccess(user);
    const path = pathname.split("/").pop();

    if (path === "pending-approvals" && !canOpenPendingApprovals) {
      navigate("/rfqs/workspace/my-workspace", { replace: true });
      return;
    }
    const workspaceQueryParams = new URLSearchParams(location.search);
    const requestedStatus = normalizeWorkspaceStatus(workspaceQueryParams.get("status"));
    const requestedProjectId = String(workspaceQueryParams.get("projectId") || "").trim();
    const requestedRfqTypes = normalizeWorkspaceRfqTypes(workspaceQueryParams.get("rfqType"));
    const requestedCategoryFilter = normalizeWorkspaceCategoryFilter(workspaceQueryParams);
    const requestedRequesterId = String(workspaceQueryParams.get("requesterId") || workspaceQueryParams.get("userId") || "").trim();
    const requestedDashboardEntityId = String(workspaceQueryParams.get("dashboardEntityId") || "").trim();
    const requestedStartDate = String(workspaceQueryParams.get("startDate") || "").trim();
    const requestedEndDate = String(workspaceQueryParams.get("endDate") || "").trim();
    const hasRequestedDateRange = Boolean(requestedStartDate || requestedEndDate);
    const resolvedRequestedEndDate = hasRequestedDateRange ? (requestedEndDate || today) : "";
    const dashboardWorkspaceRequesterId = hasDashboardAccess &&
      requestedDashboardEntityId === dashboardEntityId &&
      requestedDashboardEntityId === entityId &&
      getWorkspaceEntityMemberIds(user).includes(requestedRequesterId)
      ? requestedRequesterId
      : "";

    let initialWorkspaceType = "MY_WORKSPACE";
    let initialWorkspaceView = "My Workspace";
    let initialUrl = "/rfqs/workspace/my-workspace";
    let initialStatus = requestedStatus !== null ? requestedStatus : "OPEN";

    // Respect direct route access
    switch (path) {
      case "team-workspace":
        initialWorkspaceType = "TEAM_WORKSPACE";
        initialWorkspaceView = "Team Workspace";
        initialUrl = "/rfqs/workspace/team-workspace";
        initialStatus = requestedStatus !== null ? requestedStatus : "OPEN";
        break;
      case "pending-approvals":
        initialWorkspaceType = "PENDING_APPROVALS";
        initialWorkspaceView = "Pending Approvals";
        initialUrl = "/rfqs/workspace/pending-approvals";
        initialStatus = requestedStatus !== null ? requestedStatus : "PENDING APPROVAL";
        break;
      case "approved-by-me":
        initialWorkspaceType = "APPROVED_BY_ME";
        initialWorkspaceView = "Approved RFQs";
        initialUrl = "/rfqs/workspace/approved-by-me";
        initialStatus = "";
        break;
      case "my-workspace":
        initialWorkspaceType = "MY_WORKSPACE";
        initialWorkspaceView = "My Workspace";
        initialUrl = "/rfqs/workspace/my-workspace";
        initialStatus = requestedStatus !== null ? requestedStatus : "OPEN";
        break;
      default:
        // Only redirect if it's bare /rfqs
        if (pathname === "/rfqs") {
          if (canOpenPendingApprovals) {
            initialWorkspaceType = "PENDING_APPROVALS";
            initialWorkspaceView = "Pending Approvals";
            initialUrl = "/rfqs/workspace/pending-approvals";
            initialStatus = "PENDING APPROVAL";
          } else {
            initialWorkspaceType = "MY_WORKSPACE";
            initialWorkspaceView = "My Workspace";
            initialUrl = "/rfqs/workspace/my-workspace";
            initialStatus = "OPEN";
          }
          navigate(initialUrl, { replace: true });
        }
        break;
    }

    //  Set current workspace state
    setWorkspaceView(initialWorkspaceView);
    setRfqFilterStatus(initialStatus);
    setWorkspaceProjectId(requestedProjectId);
    setSelectedRfqTypes(requestedRfqTypes);
    setWorkspaceCategoryFilter(requestedCategoryFilter);
    setSelectedProjectNames([]);
    setStartDate(requestedStartDate);
    setEndDate(resolvedRequestedEndDate);
    setDateFilter(hasRequestedDateRange);

    //  Dispatch unified API
    dispatch(
      searchRfqWithDate({
        entityId,
        searchParam: "",
        projectId: requestedProjectId,
        status: initialStatus,
        userIds: dashboardWorkspaceRequesterId
          ? [dashboardWorkspaceRequesterId]
          : initialWorkspaceType === "MY_WORKSPACE"
            ? [userIdString]
            : [userIdString, ...teamUserIdsInString],
        startDate: requestedStartDate,
        endDate: resolvedRequestedEndDate,
        startDateOfClosing: "",
        endDateOfClosing: "",
        dateFilterIsActive: hasRequestedDateRange,
        closeDateFilterIsActive: false,
        rfqTypes: requestedRfqTypes,
        categoryFilter: requestedCategoryFilter,
        workspaceType: initialWorkspaceType,
        approverId: user?.userId,
        teamUserIds: teamUserIdsInString,
      })
    );

    dispatch(fetchRfqProjects(entityId));

    //  Handle form visibility
    if (location.pathname === "/rfqs/create") {
      setShowForm(true);
    } else {
      setShowForm(false);
    }
  }, [pathname, location.search, user, entityId]);

  useEffect(() => {
    if (pathname !== "/rfqs/create") return;

    const queryParams = new URLSearchParams(location.search);
    const rfqType = queryParams.get("rfqType");

    if (rfqType) {
      setRfqFormValues((prevValues) => ({
        ...prevValues,
        requirementType: rfqType, // Update the requirementType based on URL
      }));
      setShowForm(true); // Open the form if rfqType exists
    }
  }, [pathname, location.search]);

  useEffect(() => {
    if (currentPage === "/rfqs/create") {
      setShowForm(true);
    }
    if (currentPage === "/rfqs") {
      setShowForm(false);
    }
  }, [currentPage]);

  useEffect(() => {
    // console.log("USER", user)
    if (user?.userEntity?.licenseStatus === "EXPIRED") {
      dispatch(signout())
      navigate("/");
    }

  }, [user?.userEntity])

  useEffect(() => {
    setRfqSearchValue(rfqsData?.searchParam ?? "");
    setRfqSearchKey(rfqsData?.searchParam ?? "");
  }, [rfqsData?.searchParam]);

  if (pathname.endsWith("/pending-approvals") && !canAccessPendingApprovals) {
    return <Navigate replace to="/rfqs/workspace/my-workspace" />;
  }

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  // If on mobile and viewing pending approvals, show only mobile view
  if (isMobile && workspaceView === "Pending Approvals") {
    return (
      <MobilePendingApprovalsView
        rfqsData={rfqsData}
        user={user}
        entityId={entityId}
        teamUserIdsInString={teamUserIdsInString}
        startDate={startDate}
        endDate={endDate}
      />
    );
  }
  return (
    <SidePanel>
      <div className="h-screen overflow-hidden bg-gradient-to-r from-[#FFF0E4] to-[#E4E7FF] px-4 py-3">
        <div className="flex h-full min-h-0 gap-3">
          {showFiltersPanel && (
            <div className="h-full w-[224px] shrink-0">
              <FilterSection
                handleChangeTeamMemberNew={handleChangeTeamMemberNew}
                workspaceView={workspaceView}
                rfqsData={rfqsData}
                handleGetRfqs={handleGetRfqs}
                setRfqFilterStatus={setRfqFilterStatus}
                rfqFilterStatus={rfqFilterStatus}
                user={user}
                handleApplyFilterDate={handleApplyFilterDate}
                clearDateFilter={clearDateFilter}
                clearFilter={clearFilter}
                setSelectedRfqTypes={setSelectedRfqTypes}
                selectedRfqTypes={selectedRfqTypes}
                getWorkspaceType={getWorkspaceType}
                teamUserIdsInString={teamUserIdsInString}
                businessDivisionOptions={businessDivisionOptions}
                selectedBusinessDivisions={selectedBusinessDivisions}
                setSelectedBusinessDivisions={setSelectedBusinessDivisions}
                selectedProjectNames={selectedProjectNames}
                setSelectedProjectNames={setSelectedProjectNames}
                selectedCategoryFilter={workspaceCategoryFilter}
                setSelectedCategoryFilter={setWorkspaceCategoryFilter}
                onClose={() => setShowFiltersPanel(false)}
                projectId={workspaceProjectId}
              />
            </div>
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="shrink-0 rounded-xl bg-white px-3 pt-3 shadow-sm">
          <div className="flex w-full flex-wrap items-center gap-3">
           <button
              type="button"
              onClick={() => setShowFiltersPanel((value) => !value)}
              className={`relative inline-flex items-center gap-2 rounded-md border py-2 px-2 text-xs font-semibold transition ${
                showFiltersPanel
                  ? "border-[#E95420] bg-[#FFF4EE] text-[#B43A12]"
                  : "border-[#DADAD0] bg-white text-[#303026] hover:border-[#E95420] hover:text-[#B43A12]"
              }`}
            >
              {/* Ping Ball */}
              <span className="absolute -left-1 -top-1 flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#E95420] opacity-40"></span>
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#E95420]"></span>
              </span>

              <svg
                className="h-4 w-4"
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M3 5h14M6 10h8M9 15h2"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>

              Filters
            </button>
            <div className="min-w-[280px] flex-1">
              <div className="relative">
                <svg
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M8.75 15.5a6.75 6.75 0 1 1 0-13.5 6.75 6.75 0 0 1 0 13.5Zm5.05-1.7 3.2 3.2"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
                <input
                  type="text"
                  value={rfqSearchValue}
                  onChange={handleRfqSearchChange}
                  onKeyDown={handleRfqSearchKeyDown}
                  placeholder="Search by Vendor Name or RFQ No."
                  className="w-full rounded-md border border-[#D9D9CE] bg-white pl-10 pr-4 text-xs text-[#303026] outline-none transition placeholder:text-[#7C8493] focus:border-[#E95420] focus:ring-1 focus:ring-[#F8B090]"
                />
              </div>
            </div>

            {/* {!dateFilter ? (
                <button
                  type="button"
                  className="m-3 rounded-md border border-blue-700 text-blue-700 hover:text-blue-800 hover:border-blue-800 shadow-sm px-2 py-1 text-sm font-medium focus:outline-none"
                  onClick={() => {
                    setDateFilter(true);
                    setEndDate(today);
                  }}
                >
                  Date Range
                </button>
              ) : (
                <div className="m-3 my-0 rounded-lg flex gap-2 items-center p-1 relative">
                  <div className="flex flex-col">
                    <input
                      type="date"
                      id="start-date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      max={new Date().toISOString().split("T")[0]}
                      className="border border-gray-300 rounded-md p-2"
                    />
                  </div>
                  <p>To</p>
                  <div className="flex flex-col">
                    <input
                      type="date"
                      id="end-date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      max={new Date().toISOString().split("T")[0]}
                      className="border border-gray-300 rounded-md p-2"
                    />
                  </div>
                </div>
              )} */}
              {/* <DateRangePicker
                handleApplyFilterDate={handleApplyFilterDate}
                clearDateFilter={clearDateFilter}
                clearFilter={clearFilter}
              /> */}

            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                className={`flex items-center justify-center gap-2 rounded-md border px-4 py-2 text-xs font-medium ${
                  hasDashboardAccess
                    ? "border-[#1F1E29] bg-white text-[#1F1E29]"
                    : "cursor-not-allowed border-gray-300 bg-gray-100 text-gray-400"
                }`}
                onClick={handleOpenDashboard}
                disabled={!hasDashboardAccess}
                title={
                  hasDashboardAccess
                    ? "Open Dashboard"
                    : "Dashboard access is not available for your account"
                }
              >
                View Dashboard
                <svg
                  className="h-3.5 w-3.5"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path d="M3 16.25H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M5.5 14V10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M10 14V7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M14.5 14V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M4.75 7.75L8.25 5.75L11 8L15.25 4.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <button
                className="flex items-center justify-center rounded-md bg-[#1F1E29] px-4 py-2 text-xs font-medium text-white"
                onClick={toggleDropdown}
              >
                <svg
                  className="h-3.5 w-3.5 mr-2 -ml-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    clipRule="evenodd"
                    fillRule="evenodd"
                    d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                  />
                </svg>
                Create Requirement
              </button>
            </div>
            <RFQTypeDropDown
              showDropdown={showDropdown}
              setShowDropdown={setShowDropdown}
              handleRequirementTypeSelect={handleRequirementTypeSelect}
            />
          </div>

          <div className="mt-4 flex w-full flex-wrap items-end justify-between gap-3">
            <div className="flex min-w-0 gap-6 overflow-x-auto text-sm px-1">
              <button
                className={`whitespace-nowrap pb-1 transition ${workspaceView === "My Workspace"
                  ? "border-b-2 border-orange-500 font-semibold text-black"
                  : "text-gray-600 hover:text-black"
                  }`}
                onClick={fetchPersonalRfqs}
              >
                My Workspace
                {workspaceView !== "My Workspace" &&
                  rfqsData?.workspaceWiseCounts?.myWorkspaceCount > 0 &&
                  ` (${rfqsData?.workspaceWiseCounts?.myWorkspaceCount})`}
              </button>

              {user?.teamMembers?.length > 0 && (
                <button
                  className={`whitespace-nowrap pb-1 transition ${workspaceView === "Team Workspace"
                    ? "border-b-2 border-orange-500 font-semibold text-black"
                    : "text-gray-600 hover:text-black"
                    }`}
                  onClick={fetchAllRfqs}
                >
                  Team Workspace
                  {workspaceView !== "Team Workspace" &&
                    rfqsData?.workspaceWiseCounts?.teamWorkspaceCount > 0 &&
                    ` (${rfqsData?.workspaceWiseCounts?.teamWorkspaceCount})`}
                </button>
              )}

              {canAccessPendingApprovals && (
                <button
                  className={`whitespace-nowrap pb-1 transition ${workspaceView === "Pending Approvals"
                    ? "border-b-2 border-orange-500 font-semibold text-black"
                    : "text-gray-600 hover:text-black"
                    }`}
                  onClick={fetchPendingApprovalRfqs}
                >
                  Pending Approvals
                  {workspaceView !== "Pending Approvals" &&
                    rfqsData?.workspaceWiseCounts?.pendingApprovalsCount > 0 &&
                    ` (${rfqsData?.workspaceWiseCounts?.pendingApprovalsCount})`}
                </button>
              )}

              {user?.IAM_role === "Manager" && (
                <button
                  className={`whitespace-nowrap pb-1 transition ${workspaceView === "Approved RFQs"
                    ? "border-b-2 border-orange-500 font-semibold text-black"
                    : "text-gray-600 hover:text-black"
                    }`}
                  onClick={fetchApprovedRfqs}
                >
                  Approved By Me
                  {workspaceView !== "Approved RFQs" &&
                    rfqsData?.workspaceWiseCounts?.approvedByMeCount > 0 &&
                    ` (${rfqsData?.workspaceWiseCounts?.approvedByMeCount})`}
                </button>
              )}
            </div>
            <p className="text-xs py-2 px-4 text-[#4B5563]">
              {getShowingRfqsLabel()}
            </p>
          </div>
        </div>

            <div className="min-h-0 flex-1 rounded-xl bg-white shadow-sm">
              {/* Mobile View for Pending Approvals */}
              {workspaceView === "Pending Approvals" && (
                <div className="md:hidden">
                  <MobilePendingApprovalsView
                    rfqsData={rfqsData}
                    user={user}
                    entityId={entityId}
                    teamUserIdsInString={teamUserIdsInString}
                    startDate={startDate}
                    endDate={endDate}
                  />
                </div>
              )}
              <RfqsTableSection
                handleGetRfqs={handleGetRfqs}
                workspaceView={workspaceView}
                entityId={entityId}
                userIds={teamMemberData}
                rfqsData={rfqsData}
                rfqFilterStatus={rfqFilterStatus}
                user={user}
                fetchFilterDateRfq={fetchFilterDateRfq}
                dateFilterOn={startDate != "" && endDate != ""}
                startDate={startDate}
                endDate={endDate}
                isApprovalRequired={isApprovalRequired}
                getWorkspaceType={getWorkspaceType}
                teamUserIdsInString={teamUserIdsInString}
                projectId={workspaceProjectId}
                hideToolbar
              />
            </div>
          </div>
        </div>
      </div>

        {/* <SlideInForm showForm={showForm} closeForm={() => setShowForm(false)}> */}
        <div
          className={`fixed inset-0 z-30 flex justify-end transition-all duration-500 ${showForm ? "opacity-100 visible" : "opacity-0 invisible"
            }`}
        >
          <div className="fixed inset-0 bg-black opacity-50" />
          <div
            className={`fixed z-20 top-0 right-0 h-full w-[94%] bg-white transition-transform duration-500 transform ${showForm ? "translate-x-0" : "translate-x-full"
              }`}
          >
            {/* <div className="fixed inset-0 bg-black opacity-50"  /> */}

            <Formik
              enableReinitialize
              initialValues={{
                ...rfqFormValues,
                // workStartDate: Date(Date.now()),
                requesterName: user?.name || "",
                requesterMobile: user?.mobile || "",
                buyerPublicContact: user?.publicContact || "",
                userBusinessName: user?.userEntity
                  ? user?.userEntity.entityName
                  : user?.company || "",
                requestedBy: user?.email || "",
                requesterId: user?.userId || "",
                isEntityUser: user?.userEntity ? true : false,
                // rfqDetails: "rfqDetails"
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ values, submitForm, errors, resetForm }) => (
                <Form autoComplete="off" className="h-full justify-start">
                  {/* header */}
                  <div className="sticky top-0 bg-white z-20">
                    <div className="flex px-8 py-3 justify-between items-center">
                      <h1 className="text-neevay-theme-text-600 font-medium text-xl">
                        Requirement Form :{" "}
                        {values?.requirementType === "labour"
                          ? "Labour/Item Rate"
                          : values?.requirementType === "contractor"
                            ? "Contractor with Material"
                            : values?.requirementType === "material"
                              ? "Material Only"
                              : capitalizeFirstLetter(values?.requirementType)}
                      </h1>

                      <div className="flex items-center gap-2">
                        {materialMasterEnabled && values?.requirementType === "material" && (
                          <button
                            type="button"
                            onClick={() => setMaterialMasterFetchRequest((request) => request + 1)}
                            className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 transition hover:border-blue-300 hover:bg-blue-100"
                          >
                            <Inventory2OutlinedIcon sx={{ fontSize: 14 }} />
                            Master List
                          </button>
                        )}
                        {isPendingPrMaterialIntegration && values?.requirementType === "material" && (
                          <button
                            type="button"
                            onClick={() => setPendingPrFetchRequest((request) => request + 1)}
                            className="inline-flex items-center gap-1.5 rounded-md border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-700 transition hover:border-orange-300 hover:bg-orange-100"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <path d="M4 7h16M4 12h16M4 17h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                            Fetch Pending PR
                          </button>
                        )}
                        <Button
                          variant="text"
                          className="text-gray-600 text-sm flex items-center gap-x-2"
                          style={{ background: "#FBFBF9" }}
                          onClick={() => {
                            resetForm();
                            setRfqFormValues(getDefaultRfqFormValues());
                            setRfqData(null);
                            setPendingInviteAfterSave(false);
                            setPendingRecommendationCategory(null);
                            setRecommendedSelectedVendors([]);
                            setOpenRecommendations(false);
                            setShowForm(false);
                            navigate("/rfqs");
                          }}
                        >
                          Close
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M1 1L11 11M11 1L1 11"
                              stroke="#4F4F42"
                              strokeLinecap="round"
                            />
                          </svg>
                        </Button>
                      </div>
                    </div>
                    <div>
                      <hr />
                    </div>
                  </div>

                  <div className="h-full overflow-y-auto pb-[100px]">
                    <GeneralInformationNew
                      projects={projects}
                      updateFormValues={updateRfqFormValues}
                      entityId={entityId}
                      advancedTransportOptions={advancedTransportOptions}
                      showBusinessDivisions={showBusinessDivisions}
                      businessDivisionOptions={businessDivisionOptions}
                      multipleProjectSelection={multipleProjectSelection}
                      indentIntegrationEnabled={isIndentMaterialIntegration}
                      indentApiUrl={indentApiUrl}
                      pendingPrIntegrationEnabled={isPendingPrMaterialIntegration}
                      pendingPrFetchRequest={pendingPrFetchRequest}
                      materialMasterEnabled={materialMasterEnabled}
                      materialMasterFetchRequest={materialMasterFetchRequest}
                    />
                  </div>
                  {/* 
                   <RfqDetailsSection
                   selectedFilesBase={selectedFilesBase}
                   setSelectedFilesBase={setSelectedFilesBase}
                   fieldName="rfqDetails"
                   updateFormValues={updateRfqFormValues}
                   /> */}

                  {/* footer */}
                  <div
                    className="px-3 py-4 sticky bottom-0 z-20 bg-white flex justify-between items-center shadow-md "
                    style={{
                      boxShadow: "0px -4px 4px 0px #0000001A",
                    }}
                  >
                    {/* <Button
                  variant="text"
                  className="text-neevay-theme-text-400 flex items-center gap-x-2 hover:text-neevay-theme-text-500"
                  onClick={() => {
                    setHasMoreOptions(!hasMoreOptions);
                  }}
                  style={{ background: "#F5F5F3" }}
                  type="button"
                >
                  {hasMoreOptions ? "Less Options" : "More Options"}
                </Button> */}
                    {/* {!openAddVendorOption && (
                  <button
                    type="button"
                    className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg mr-2"
                    disabled={loading} // Disable the button when loading is true
                    onClick={async (e) => {
                      e.preventDefault();
                      await submitForm();
                      const firstErrorField = Object.keys(errors)[0];
                      const errorElement =
                        document.getElementById(firstErrorField);
                      if (errorElement) {
                        errorElement.scrollIntoView({
                          behavior: "smooth",
                        });
                      } else if (values.rfqName) {
                        setShowForm(false);
                        navigate("/rfqs");
                        handleCloseRfqForm();
                      }
                    }}
                  >
                    {loading ? "Saving Draft..." : "Save as Draft"}
                  </button>
                )} */}
                    <div className="space-x-2 sticky bottom-0 flex justify-end w-full px-8">
                      <button
                        type="button"
                        className="px-4 border rounded-md border-[#1E1E08] text-sm w-fit whitespace-nowrap py-2 hover:bg-[#1E1E08] hover:text-white mr-2 "
                        disabled={loading || rfqData?.rfqId} // Disable the button when loading is true
                        onClick={async (e) => {
                          e.preventDefault();
                          setPendingInviteAfterSave(false);
                          setPendingRecommendationCategory(null);
                          setRecommendedSelectedVendors([]);
                          await submitForm();
                          const firstErrorField = Object.keys(errors)[0];
                          const errorElement =
                            document.getElementById(firstErrorField);
                          if (errorElement) {
                            errorElement.scrollIntoView({
                              behavior: "smooth",
                            });
                          } else if (values.rfqName) {
                            if (Object.keys(errors).length === 0) {
                              setShowForm(false);
                              navigate("/rfqs");
                              setRfqFormValues(getDefaultRfqFormValues());
                              setRfqData(null);
                              handleCloseRfqForm();
                            }
                          }
                        }}
                      >
                        {loading ? "Saving Draft..." : "Save as Draft"}
                      </button>

                      <button
                        className={`border hover:bg-black hover:text-white hover:border-black ${loading
                          ? " bg-gray-300"
                          : " bg-[#1E1E08] hover:bg-[#1A1A02]"
                          } text-white rounded-md px-4 py-2 font-medium flex justify-evenly items-center`}
                        type="button"
                        disabled={loading || rfqData?.rfqId}
                        onClick={async (e) => {
                          e.preventDefault();
                          setPendingInviteAfterSave(true);
                          setPendingRecommendationCategory(values?.category || null);
                          setRecommendedSelectedVendors([]);
                          await submitForm();

                          const firstErrorField = Object.keys(errors)[0];
                          const errorElement =
                            document.getElementById(firstErrorField);
                          if (errorElement) {
                            setPendingInviteAfterSave(false);
                            errorElement.scrollIntoView({
                              behavior: "smooth",
                            });
                          } else if (values.rfqName) {
                            if (Object.keys(errors).length === 0) {
                              if (rfqData?.rfqId) {
                                setOpenRecommendations(true);
                                setPendingInviteAfterSave(false);
                              }
                              // if (marketPlaceAccess) {
                              //   setOpenAddVendorOption(true); // Show both options if marketPlaceAccess is true
                              // } else {
                              //   handleSliderOpen("CD"); // Directly open the Directory if marketPlaceAccess is false
                              // }
                            }
                          } else {
                            setPendingInviteAfterSave(false);
                          }
                          // updateRfqFormValues(values);
                          // setPressSaveOnly(false);
                        }}
                      >
                        <span className="text-sm font-normal">
                          Save & Invite Vendors
                        </span>
                        {/* <hr className="rotate-90 w-[13px] h-[1px] border-none bg-[#969696] my-2" />
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 10 10"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g clip-path="url(#clip0_4661_4356)">
                            <path
                              d="M4.98894 2.5C5.22762 2.50005 5.4565 2.5877 5.62524 2.74367L9.22524 6.07019C9.3112 6.1469 9.37976 6.23867 9.42693 6.34013C9.4741 6.44159 9.49893 6.55072 9.49997 6.66114C9.501 6.77157 9.47823 6.88108 9.43298 6.98328C9.38773 7.08548 9.3209 7.17834 9.2364 7.25642C9.15189 7.3345 9.05141 7.39626 8.9408 7.43807C8.83019 7.47989 8.71168 7.50093 8.59218 7.49997C8.47268 7.49901 8.35458 7.47607 8.24478 7.43248C8.13497 7.3889 8.03566 7.32554 7.95264 7.24611L4.98894 4.50755L2.02524 7.24611C1.8555 7.3976 1.62816 7.48142 1.39218 7.47953C1.1562 7.47763 0.930473 7.39017 0.763606 7.23598C0.596738 7.08179 0.502085 6.87321 0.500035 6.65516C0.497984 6.43711 0.588699 6.22704 0.752641 6.07019L4.35264 2.74367C4.52139 2.5877 4.75027 2.50005 4.98894 2.5Z"
                              fill="#FD6600"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_4661_4356">
                              <rect
                                width="10"
                                height="10"
                                fill="white"
                                transform="matrix(-1 0 0 -1 10 10)"
                              />
                            </clipPath>
                          </defs>
                        </svg> */}
                      </button>

                      {/* // up dropdown of company directory and neevay network -----------------------start5 from here */}
                      {openAddVendorOption &&
                        !loading &&
                        rfqData?.rfqId > 0 && marketPlaceAccess && (
                          <div className="absolute rounded-lg py-2 bottom-[110%] right-0 w-52 bg-white border  border-gray-500">
                            <div
                              className="py-2 pl-3 hover:bg-gray-100 cursor-pointer"
                              onClick={() => handleSliderOpen("CD")}
                            >
                              <p className="text-left font-medium text-gray-900">
                                {directoryName} Directory{" "}
                              </p>
                            </div>
                            <div
                              className="py-2 pl-3 flex items-center hover:bg-gray-100 cursor-pointer"
                              onClick={() => handleSliderOpen("NN")}
                            >
                              <div
                                style={{
                                  maxWidth: "250px",
                                  marginRight: "8px",
                                }}
                              >
                                <p className="font-medium text-gray-900">
                                  Neevay Network{" "}
                                </p>
                              </div>

                              <div
                                style={{
                                  borderRadius: "3px",
                                  border: "0.5px solid #039400",
                                  background: "#E1FFDE",
                                  padding: "0 4px", // You can adjust the padding as needed
                                }}
                                className="flex items-center"
                              >
                                <span className="text-black font-inter font-semibold text-xs ">
                                  N
                                </span>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="5"
                                  height="5"
                                  viewBox="0 0 5 5"
                                  fill="none"
                                >
                                  <circle
                                    cx="2.5"
                                    cy="2.5"
                                    r="2.5"
                                    fill="#039400"
                                  />
                                </svg>
                              </div>
                            </div>
                          </div>
                        )}
                      {/* // up dropdown of company directory and neevay network -----------------------end from here */}
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
        {/* </SlideInForm> */}
        <RfqVendorRecommendations
          open={openRecommendations}
          entityId={entityId}
          rfqId={rfqData?.rfqId}
          projectId={rfqData?.projectId || rfqData?.projectName}
          rfqName={rfqData?.name || rfqFormValues?.rfqName}
          rfqType={rfqData?.rfqType || rfqFormValues?.requirementType}
          category={
            pendingRecommendationCategory?.categoryId
              ? pendingRecommendationCategory
              : rfqData?.category || pendingRecommendationCategory
          }
          projectLocation={rfqData?.location}
          invitedVendors={rfqData?.suppliers ?? []}
          maxLimit={maxVendorsAllowed}
          onClose={handleCloseRecommendations}
          onContinue={handleContinueWithRecommendedVendors}
        />
        <SelectVendorsSlider
          rfqId={rfqData?.rfqId}
          sliderName={sliderName}
          setSliderName={setSliderName}
          openSlider={openSlider}
          setOpenSlider={setOpenSlider}
          handleSliderClose={handleSliderClose}
          invitedVendors = {rfqData?.suppliers ?? []}
          fromCreateRfq={true}
          fromRfqDetails = {false}
          maxLimit={maxVendorsAllowed}
          preselectedRecommendedVendors={recommendedSelectedVendors}
        />

      <LoadingModal
        isOpen={loading || error !== null}
        onClose={() => setError(null)} // Close the modal on click
      >
        {loading ? (
          <div className="flex flex-col items-center justify-center">
            {/* This is a circular spinner centered in the modal */}
            <div className="animate-spin rounded-full border-4 border-t-4 border-orange-500 border-t-gray-200 h-8 w-8 mb-4"></div>
            <p className="text-xl font-medium  text-gray-600 italic">
              Sit tight, we are creating RFQ for you...
            </p>
          </div>
        ) : (
          <div>
            <p>{error || "RFQ saved successfully!"}</p>
          </div>
        )}
      </LoadingModal>
    </SidePanel>
  );
};

export default RfqsSections;
